import api from '../api';
import { UserPronunciationProfile, SpeakingAnalytics, ProgressTimeline } from './types';

const baseURL = '/speaking/v1';

class AnalyticsService {
    // ============== USER PROFILE & ANALYTICS ==============

    async getUserPronunciationProfile(): Promise<UserPronunciationProfile> {
        const response = await api.get(`${baseURL}/user-profile/`);
        return response.data;
    }

    async getSpeakingAnalytics(days: number = 30): Promise<SpeakingAnalytics> {
        const response = await api.get(`${baseURL}/analytics/?days=${days}`);
        return response.data;
    }

    async getProgressTimeline(days: number = 90): Promise<ProgressTimeline> {
        const response = await api.get(`${baseURL}/progress-timeline/?days=${days}`);
        return response.data;
    }

    // ============== PROGRESS CALCULATIONS ==============

    calculateOverallProgress(profile: UserPronunciationProfile): {
        currentScore: number;
        targetScore: number;
        progressPercentage: number;
        weeklyGrowth: number;
        monthlyGrowth: number;
    } {
        const currentScore = profile.phoneme_accuracy * 0.4 + profile.fluency_score * 0.3 + profile.prosody_score * 0.3;

        const targetScores = {
            beginner: 60,
            elementary: 70,
            intermediate: 80,
            upper_intermediate: 85,
            advanced: 90,
            native_like: 95,
        };

        const targetScore = targetScores[profile.overall_pronunciation_level] || 70;
        const progressPercentage = Math.min(100, (currentScore / targetScore) * 100);

        // Calculate growth trends
        const weeklyScores = profile.weekly_scores || [];
        const weeklyGrowth =
            weeklyScores.length >= 2
                ? weeklyScores[weeklyScores.length - 1] - weeklyScores[weeklyScores.length - 2]
                : 0;

        const monthlyProgress = profile.monthly_progress || [];
        const monthlyGrowth =
            monthlyProgress.length >= 2
                ? (monthlyProgress[monthlyProgress.length - 1] as any)?.score -
                      (monthlyProgress[monthlyProgress.length - 2] as any)?.score || 0
                : 0;

        return {
            currentScore: Math.round(currentScore),
            targetScore,
            progressPercentage: Math.round(progressPercentage),
            weeklyGrowth: Math.round(weeklyGrowth * 10) / 10,
            monthlyGrowth: Math.round(monthlyGrowth * 10) / 10,
        };
    }

    calculatePhonemeProgress(analytics: SpeakingAnalytics): {
        improving: Array<{ phoneme: string; trend: number }>;
        struggling: Array<{ phoneme: string; accuracy: number }>;
        mastered: Array<{ phoneme: string; accuracy: number }>;
    } {
        const improving: Array<{ phoneme: string; trend: number }> = [];
        const struggling: Array<{ phoneme: string; accuracy: number }> = [];
        const mastered: Array<{ phoneme: string; accuracy: number }> = [];

        analytics.phoneme_breakdown.forEach((phoneme) => {
            if (phoneme.accuracy_rate > 90) {
                mastered.push({
                    phoneme: phoneme.phoneme,
                    accuracy: phoneme.accuracy_rate,
                });
            } else if (phoneme.accuracy_rate < 60) {
                struggling.push({
                    phoneme: phoneme.phoneme,
                    accuracy: phoneme.accuracy_rate,
                });
            }

            // Calculate trend based on recent vs older data
            // This would need more historical data in a real implementation
            if (phoneme.avg_accuracy > 70) {
                improving.push({
                    phoneme: phoneme.phoneme,
                    trend: 0.1, // Mock improvement trend
                });
            }
        });

        return {
            improving: improving.sort((a, b) => b.trend - a.trend),
            struggling: struggling.sort((a, b) => a.accuracy - b.accuracy),
            mastered: mastered.sort((a, b) => b.accuracy - a.accuracy),
        };
    }

    // ============== STREAK CALCULATIONS ==============

    calculateStreakMetrics(profile: UserPronunciationProfile): {
        currentStreak: number;
        longestStreak: number;
        streakType: 'daily' | 'weekly' | 'none';
        daysUntilNextMilestone: number;
        motivationalMessage: string;
    } {
        const currentStreak = profile.streak_days;
        const longestStreak = profile.longest_streak;

        let streakType: 'daily' | 'weekly' | 'none' = 'none';
        if (currentStreak >= 7) {
            streakType = 'weekly';
        } else if (currentStreak >= 1) {
            streakType = 'daily';
        }

        // Calculate days until next milestone
        const milestones = [7, 14, 30, 60, 100, 365];
        const nextMilestone = milestones.find((m) => m > currentStreak) || milestones[milestones.length - 1];
        const daysUntilNextMilestone = nextMilestone - currentStreak;

        // Generate motivational message
        const messages = {
            0: 'Start your speaking practice streak today! 🚀',
            1: 'Great start! Keep going to build momentum! 💪',
            3: "You're building a habit! Three days strong! 🔥",
            7: "Amazing! You've completed a full week! 🌟",
            14: "Two weeks of dedication! You're unstoppable! 🎉",
            30: "One month streak! You're a pronunciation champion! 🏆",
            60: 'Two months of consistent practice! Incredible! 🥇',
            100: "100 days! You're in the pronunciation hall of fame! 👑",
        };

        const motivationalMessage =
            messages[currentStreak as keyof typeof messages] ||
            `${currentStreak} days strong! Keep up the amazing work! 🌟`;

        return {
            currentStreak,
            longestStreak,
            streakType,
            daysUntilNextMilestone,
            motivationalMessage,
        };
    }

    // ============== PERFORMANCE INSIGHTS ==============

    generatePerformanceInsights(analytics: SpeakingAnalytics): {
        strengths: string[];
        weaknesses: string[];
        recommendations: string[];
        nextFocus: string;
    } {
        const strengths: string[] = [];
        const weaknesses: string[] = [];
        const recommendations: string[] = [];

        // Analyze overall stats
        const { overall_stats } = analytics;

        if (overall_stats.average_score > 80) {
            strengths.push('Excellent overall pronunciation accuracy');
        }

        if (overall_stats.improvement_trend > 0) {
            strengths.push('Consistent improvement over time');
        } else if (overall_stats.improvement_trend < -2) {
            weaknesses.push('Recent decline in performance');
            recommendations.push('Review fundamental pronunciation techniques');
        }

        // Analyze phoneme performance
        const strugglingPhonemes = analytics.phoneme_breakdown.filter((p) => p.accuracy_rate < 60).slice(0, 3);

        if (strugglingPhonemes.length > 0) {
            weaknesses.push(`Difficulty with: ${strugglingPhonemes.map((p) => p.phoneme).join(', ')}`);
            recommendations.push('Focus on minimal pairs and targeted phoneme practice');
        }

        // Analyze error patterns
        const topErrors = Object.entries(analytics.error_patterns)
            .sort(([, a], [, b]) => b - a)
            .slice(0, 2);

        if (topErrors.length > 0) {
            weaknesses.push(`Common error patterns: ${topErrors.map(([error]) => error).join(', ')}`);
        }

        // Generate next focus area
        let nextFocus = 'Continue regular practice';
        if (strugglingPhonemes.length > 0) {
            nextFocus = `Focus on ${strugglingPhonemes[0].phoneme} sound`;
        } else if (overall_stats.average_score < 75) {
            nextFocus = 'Work on overall pronunciation clarity';
        } else {
            nextFocus = 'Practice advanced prosody and intonation';
        }

        return {
            strengths,
            weaknesses,
            recommendations,
            nextFocus,
        };
    }

    // ============== COMPARATIVE ANALYSIS ==============

    compareToTarget(
        current: number,
        target: number,
    ): {
        status: 'below' | 'at' | 'above';
        gap: number;
        percentage: number;
        message: string;
    } {
        const gap = target - current;
        const percentage = (current / target) * 100;

        let status: 'below' | 'at' | 'above' = 'below';
        let message = '';

        if (current >= target) {
            status = 'above';
            message = "Excellent! You've exceeded your target level!";
        } else if (percentage >= 95) {
            status = 'at';
            message = "Great! You're very close to your target!";
        } else {
            status = 'below';
            if (percentage >= 80) {
                message = 'Good progress! Keep practicing to reach your target.';
            } else if (percentage >= 60) {
                message = "You're on track! Focus on consistent practice.";
            } else {
                message = 'Keep working! Every practice session brings you closer.';
            }
        }

        return {
            status,
            gap: Math.max(0, gap),
            percentage: Math.round(percentage),
            message,
        };
    }

    // ============== MILESTONE TRACKING ==============

    calculateMilestones(profile: UserPronunciationProfile): {
        completed: Array<{ name: string; date: string; score: number }>;
        upcoming: Array<{ name: string; requirement: number; progress: number }>;
        next: { name: string; requirement: number; timeEstimate: string };
    } {
        const milestones = [
            { name: 'First Steps', requirement: 50, category: 'overall' },
            { name: 'Getting Started', requirement: 60, category: 'overall' },
            { name: 'Making Progress', requirement: 70, category: 'overall' },
            { name: 'Good Speaker', requirement: 80, category: 'overall' },
            { name: 'Advanced Speaker', requirement: 90, category: 'overall' },
            { name: 'Near Native', requirement: 95, category: 'overall' },
        ];

        const currentScore = profile.intelligibility_score;

        const completed = milestones
            .filter((m) => currentScore >= m.requirement)
            .map((m) => ({
                name: m.name,
                date: profile.updated_at, // Would need actual achievement date
                score: currentScore,
            }));

        const upcoming = milestones
            .filter((m) => currentScore < m.requirement)
            .map((m) => ({
                name: m.name,
                requirement: m.requirement,
                progress: (currentScore / m.requirement) * 100,
            }));

        const next = upcoming[0] || {
            name: 'Master Speaker',
            requirement: 100,
            timeEstimate: 'Keep practicing!',
        };

        // Estimate time to next milestone based on improvement trend
        const recentImprovement = profile.recent_improvement || 0;
        let timeEstimate = 'Keep practicing!';

        if (recentImprovement > 0 && upcoming.length > 0) {
            const pointsNeeded = upcoming[0].requirement - currentScore;
            const weeksEstimate = Math.ceil(pointsNeeded / recentImprovement);
            timeEstimate = `~${weeksEstimate} weeks with current progress`;
        }

        return {
            completed,
            upcoming,
            next: { ...next, timeEstimate },
        };
    }

    // ============== EXPORT FUNCTIONALITY ==============

    exportProgressData(
        analytics: SpeakingAnalytics,
        profile: UserPronunciationProfile,
    ): {
        csvData: string;
        jsonData: string;
    } {
        // CSV export
        const csvHeaders = ['Date', 'Overall Score', 'Pronunciation', 'Fluency', 'Prosody'];
        const csvRows = analytics.progress_timeline.map((point) => [
            point.date,
            point.overall_score,
            point.pronunciation_accuracy,
            point.fluency_score,
            point.prosody_score,
        ]);

        const csvData = [csvHeaders, ...csvRows].map((row) => row.join(',')).join('\n');

        // JSON export
        const exportData = {
            profile: {
                level: profile.overall_pronunciation_level,
                scores: {
                    phoneme_accuracy: profile.phoneme_accuracy,
                    fluency_score: profile.fluency_score,
                    prosody_score: profile.prosody_score,
                    intelligibility_score: profile.intelligibility_score,
                },
                progress: profile.weekly_scores,
                streak: profile.streak_days,
            },
            analytics: {
                overall_stats: analytics.overall_stats,
                progress_timeline: analytics.progress_timeline,
                phoneme_breakdown: analytics.phoneme_breakdown,
            },
            exported_at: new Date().toISOString(),
        };

        const jsonData = JSON.stringify(exportData, null, 2);

        return { csvData, jsonData };
    }
}

const analyticsService = new AnalyticsService();
export default analyticsService;
